import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion, useInView } from '../hooks';
import { QrPattern } from '../mock/primitives';
import { useContent } from '../siteContent';

type Phase = 'ready' | 'scanning' | 'checking' | 'done';
const phaseText: Record<Phase, string> = {
  ready: 'جاهز للمسح',
  scanning: 'جارٍ قراءة الرمز…',
  checking: 'التحقق من الاشتراك…',
  done: 'تم تسجيل الصرف',
};

export function QrRedeem() {
  const { qr } = useContent();
  const steps = qr.steps;
  const [phase, setPhase] = useState<Phase>('ready');
  const [balance, setBalance] = useState(24);
  const timers = useRef<number[]>([]);
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.5 });

  const run = useCallback(() => {
    timers.current.forEach(window.clearTimeout); timers.current = [];
    const quick = prefersReducedMotion();
    setBalance(24);
    if (quick) { setPhase('done'); setBalance(23); return; }
    setPhase('scanning');
    timers.current.push(window.setTimeout(() => setPhase('checking'), 1300));
    timers.current.push(window.setTimeout(() => { setPhase('done'); setBalance(23); }, 2100));
  }, []);

  useEffect(() => { if (inView) run(); }, [inView, run]);
  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  if (!qr.visible) return null;
  const stepIndex = phase === 'ready' ? -1 : phase === 'scanning' ? 0 : phase === 'checking' ? 1 : 2;
  return <section className="mx-qr" aria-labelledby="qr-title">
    <div className="mx-container mx-qr-grid">
      <div className="mx-qr-copy">
        <h2 id="qr-title" className="mx-h2"><span>{qr.line1}</span><span>{qr.line2}</span></h2>
        <p className="mx-body">{qr.body}</p>
        <ol className="mx-qr-steps">
          {steps.map((step, index) => <li key={index} className={index <= stepIndex ? 'is-done' : ''}><span aria-hidden="true">{index + 1}</span><div><h3>{step.title}</h3><p>{step.text}</p></div></li>)}
        </ol>
        {qr.note && <p className="mx-qr-note">{qr.note}</p>}
      </div>
      <div ref={ref} className={`mx-qr-demo is-${phase}`}>
        <div className="mx-qr-card" aria-hidden="true">
          <div className="mx-qr-card-top"><span><small>مطعمك</small><b>بطاقة اشتراك</b></span><span className="mx-qr-pill">فعّال</span></div>
          <div className="mx-qr-code"><QrPattern /><i className="mx-qr-beam" /><span className="mx-qr-frame" /></div>
          <div className="mx-qr-card-meta"><span><small>المشترك</small><b>سارة</b></span><span><small>الرصيد</small><b>{balance} وجبة</b></span></div>
        </div>
        <div className="mx-qr-status" role="status" aria-live="polite">
          <span className="mx-qr-dot" aria-hidden="true">{phase === 'done' && <svg viewBox="0 0 20 20"><path d="M5 10.5 8.5 14 15 6.5" /></svg>}</span>
          <span><b>{phaseText[phase]}</b><small>{phase === 'done' ? 'وجبة واحدة · الفرع الرئيسي · الرصيد المتبقي ' + balance : 'محاكاة توضيحية — لا تُنفَّذ أي عملية حقيقية'}</small></span>
        </div>
        <button type="button" className="mx-btn mx-btn-light mx-btn-sm" onClick={run} disabled={phase === 'scanning' || phase === 'checking'}>أعد المحاكاة</button>
      </div>
    </div>
    {qr.statement && <div className="mx-container"><p className="mx-statement mx-statement-sm mx-on-dark">{qr.statement}</p></div>}
  </section>;
}
